import { useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { ALL_PRINT_COL_VALUES } from '@/constants/consentForm';
import { getConsentFormRelevantLabelKeys } from '@/utils/consentForm';
import type { ConsentFormData } from '@/schemas/consentSchema';
import { ensureArray } from '../utils';

interface UseConsentLabelChecksParams {
  serviceType: 'r2' | 'a2';
  setValue: UseFormSetValue<ConsentFormData>;
  watch: UseFormWatch<ConsentFormData>;
}

export const useConsentLabelChecks = ({
  serviceType,
  setValue,
  watch,
}: UseConsentLabelChecksParams) => {
  const relevantKeys = useMemo(() => {
    return getConsentFormRelevantLabelKeys(serviceType);
  }, [serviceType]);

  const allLabelKeys = useMemo(
    () =>
      Array.from(
        new Set([...relevantKeys, 'patient_address', 'patient_name', 'patient_birth_of_date'])
      ),
    [relevantKeys]
  );

  // Restore labelChecks from non_print_cols (persisted in DB).
  // A key present in non_print_cols means "do NOT print" -> checkbox unchecked.
  const nonPrintColsFromForm = ensureArray(watch('non_print_cols'));
  const [labelChecks, setLabelChecks] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(allLabelKeys.map(k => [k, !nonPrintColsFromForm.includes(k)]))
  );

  // Guard to prevent sync loop: when we write non_print_cols ourselves, skip re-init.
  const isSyncingRef = useRef(false);
  // Guard to prevent sync from running with stale labelChecks right after re-init fires.
  // Problem: re-init calls setLabelChecks (async), but sync effect runs in the SAME
  // render with old labelChecks -> would write wrong non_print_cols to the form.
  const justReInitedRef = useRef(false);

  // Re-initialize labelChecks when form data is reset (e.g. edit page loads API detail).
  const nonPrintColsKeyRef = useRef(nonPrintColsFromForm.slice().sort().join(','));
  useEffect(() => {
    if (isSyncingRef.current) {
      isSyncingRef.current = false;
      return;
    }
    const key = nonPrintColsFromForm.slice().sort().join(',');
    if (key !== nonPrintColsKeyRef.current) {
      nonPrintColsKeyRef.current = key;
      justReInitedRef.current = true;
      setLabelChecks(
        Object.fromEntries(allLabelKeys.map(k => [k, !nonPrintColsFromForm.includes(k)]))
      );
    }
  }, [nonPrintColsFromForm, allLabelKeys]);

  // Ensure labelChecks always has keys for current label set (edge-case: serviceType changes).
  useEffect(() => {
    setLabelChecks(prev => {
      const next = { ...prev };
      let changed = false;
      allLabelKeys.forEach(k => {
        if (next[k] === undefined) {
          next[k] = false;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [allLabelKeys]);

  // Watch print_cols and update labelChecks accordingly (patient info checkboxes).
  const printCols = ensureArray(watch('print_cols'));
  useEffect(() => {
    setLabelChecks(prev => {
      const next = { ...prev };
      ALL_PRINT_COL_VALUES.forEach(k => {
        next[k] = printCols?.includes(k);
      });
      return next;
    });
  }, [printCols]);

  // Sync ALL label checkboxes -> non_print_cols (unchecked keys are stored).
  // Skip when re-init just fired: labelChecks is stale in this render cycle and
  // would incorrectly overwrite non_print_cols before setLabelChecks takes effect.
  useEffect(() => {
    if (justReInitedRef.current) {
      justReInitedRef.current = false;
      return;
    }
    const unchecked = allLabelKeys.filter(k => !labelChecks[k]);
    const key = unchecked.slice().sort().join(',');
    if (key !== nonPrintColsKeyRef.current) {
      nonPrintColsKeyRef.current = key;
      isSyncingRef.current = true;
      setValue('non_print_cols', unchecked, { shouldDirty: false });
    }
  }, [labelChecks, allLabelKeys, setValue]);

  return {
    allLabelKeys,
    labelChecks,
    printCols,
    setLabelChecks,
  };
};
